import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HubHeader } from '../components/hub/HubHeader';
import { FilterBar } from '../components/hub/FilterBar';
import { DirectoryColumns } from '../components/hub/DirectoryColumns';
import { HubFaqs } from '../components/hub/HubFaqs';
import { SiblingHubs } from '../components/hub/SiblingHubs';
import { TierNote } from '../components/TierNote';
import { DesignNote } from '../components/DesignNote';
import { categories, evergreenGuides, thingsFaqs, thingsFilters, thingsIntro, thingsTier3, tier1Attractions, tier2Things } from '../data/thingsToDo';
export function ThingsToDoHub() {
  const [active, setActive] = useState<string[]>([]);
  function toggle(option: string) {
    setActive(prev => prev.includes(option) ? prev.filter(x => x !== option) : [...prev, option]);
  }
  const filtered = useMemo(() => {
    if (active.length === 0) return tier2Things;
    return tier2Things.filter(p => active.every(a => p.tags.includes(a) || p.area === a || p.category === a));
  }, [active]);
  return <main id="main" className="bg-paper" data-id="element-1131" data-mp="1mq2ogz8a9bft">
      <HubHeader crumb="Things to do" meta={`${thingsIntro.count} · ${thingsIntro.updated}`} titleLead="Things to do with kids in" titleItalic="Santa Barbara" deck={thingsIntro.deck} data-id="element-1132" data-mp="1morl30ir25p8" />

      <Doorways data-id="element-1133" data-mp="yt5epcy7jfv9" />

      <section aria-labelledby="guides-h" className="bg-paper" data-id="element-1134" data-mp="ytoxswzlgaav">
        <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-1135" data-mp="13rys75dhv6vf">
          <TierNote tier="1" label="Evergreen guides" note="This hub has two kinds of Tier 1. These four guides are the pages that rank — “beaches with kids” is searched far more than any single beach." data-id="element-1136" data-mp="13s8jcwa9zk2p" />
          <h2 id="guides-h" className="max-w-2xl font-display text-[38px] font-300 leading-[1.05] text-ink sm:text-[46px]" data-id="element-1137" data-mp="2k6rr2v524hq9">
            The guides worth reading first
          </h2>

          <ul className="mt-14 grid gap-x-14 gap-y-10 border-t border-line pt-12 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1138" data-mp="p20lckl7268z">
            {evergreenGuides.map(guide => <li key={guide.slug} className="group relative border-b border-line pb-7" data-id="element-1139" data-mp="y3i1u42ik9y7">
                <p className="label text-muted/70" data-id="element-1140" data-mp="t4nk75ga8rfo">{guide.items}</p>
                <h3 className="mt-3 font-display text-[24px] font-300 leading-snug text-ink" data-id="element-1141" data-mp="t5girj538amq">
                  <Link to={guide.slug.includes('beaches') ? '/beaches' : '/blog'} className="decoration-olive decoration-1 underline-offset-[6px] after:absolute after:inset-0 after:content-[''] group-hover:underline" data-id="element-1142" data-mp="5fm4qy918g0w">
                    {guide.title}
                  </Link>
                </h3>
                <p className="mt-3 text-[13.5px] leading-[1.75] text-muted" data-id="element-1143" data-mp="3ith4pz4pbsty">
                  {guide.blurb}
                </p>
              </li>)}
          </ul>
        </div>
      </section>

      <section aria-labelledby="places-h" className="border-t border-line bg-white" data-id="element-1144" data-mp="1tclmwbjlrr5x">
        <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-1145" data-mp="1y3bu7ubla439">
          <TierNote tier="1" label="Full place pages" note="And the other kind: the four names people already search for. These are your sponsor-ready pages." data-id="element-1146" data-mp="1y5et73upz7k4" />
          <h2 id="places-h" className="max-w-2xl font-display text-[38px] font-300 leading-[1.05] text-ink sm:text-[46px]" data-id="element-1147" data-mp="2d0uplfirvfjo">
            The four everyone asks about
          </h2>

          <ul className="mt-16 grid gap-x-16 gap-y-20 lg:grid-cols-2" data-id="element-1148" data-mp="232wzsnl9laoy">
            {tier1Attractions.map(place => <li key={place.slug} className="group relative" data-id="element-1149" data-mp="280otnn5gz6vs">
                <div className="overflow-hidden bg-creme" data-id="element-1150" data-mp="rnhvszun1ikc">
                  <img src={place.image} alt={place.imageAlt} className="photo-soft aspect-[4/3] w-full object-cover transition-transform duration-300 ease-soft group-hover:scale-[1.03]" data-id="element-1151" data-mp="rlx2tenz27ol" />
                </div>
                <p className="label mt-7 text-olive" data-id="element-1152" data-mp="174pyvqq0ckx1">{place.area}</p>
                <h3 className="mt-3 font-display text-[32px] font-300 leading-tight text-ink" data-id="element-1153" data-mp="133ego69gr1yv">
                  <Link to="/attractions" className="decoration-olive decoration-1 underline-offset-[7px] after:absolute after:inset-0 after:content-[''] group-hover:underline" data-id="element-1154" data-mp="y5cx7k84l0mv">
                    {place.name}
                  </Link>
                </h3>
                <p className="mt-4 max-w-prose text-[15.5px] leading-[1.8] text-muted" data-id="element-1155" data-mp="1ltmbm130em3z">
                  {place.hook}
                </p>
                <dl className="mt-7 grid grid-cols-4 gap-x-6 border-t border-line pt-5" data-id="element-1156" data-mp="1gv2nbu0y0y8g">
                  {[['Ages', place.ages], ['Cost', place.cost], ['Visit', place.visit], ['Indoors', place.indoor]].map(([term, value]) => <div key={term} data-id="element-1157" data-mp="b26ufxsu71z">
                      <dt className="label text-muted/70" data-id="element-1158" data-mp="c3ialiq8vk8">{term}</dt>
                      <dd className="mt-1.5 text-[13.5px] leading-snug text-ink" data-id="element-1159" data-mp="59valgnaajoy">
                        {value}
                      </dd>
                    </div>)}
                </dl>
              </li>)}
          </ul>
        </div>
      </section>

      <FilterBar filters={thingsFilters} active={active} onToggle={toggle} data-id="element-1160" data-mp="j1tar1z0p58z" />

      <section aria-labelledby="more-h" className="bg-paper" data-id="element-1161" data-mp="j3wcukdg2ou2">
        <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-1162" data-mp="o1xvx5aveo5j">
          <TierNote tier="2" label="Rich rows" note="Everything else that’s genuinely good. Filter chips act on this section." data-id="element-1163" data-mp="t0iul0ah4zrv" />
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-1164" data-mp="2urcge5b22d8b">
            <h2 id="more-h" className="max-w-2xl font-display text-[38px] font-300 leading-[1.05] text-ink sm:text-[46px]" data-id="element-1165" data-mp="2urm7jw7u6qfl">
              More places worth your morning
            </h2>
            <p className="label text-muted" role="status" data-id="element-1166" data-mp="2woudkfoxu5ey">
              {filtered.length} {filtered.length === 1 ? 'place' : 'places'}
              {active.length > 0 && ` · ${active.length} filters`}
            </p>
          </div>

          {filtered.length === 0 ? <div className="mt-14 max-w-lg" data-id="element-1167" data-mp="35nfr1wfjyu8v">
              <h3 className="font-display text-[30px] font-300 text-ink" data-id="element-1168" data-mp="30qz4v7rwypyf">
                Nothing matches all of that
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-1169" data-mp="3feskgsh01sh0">
                Try loosening the category — most of these fit more than one.
              </p>
              <button type="button" onClick={() => setActive([])} className="label mt-8 bg-ink px-7 py-3.5 text-paper transition-colors duration-150 ease-soft hover:bg-olive" data-id="element-1170" data-mp="3of8mrym0atka">
                Clear filters
              </button>
            </div> : <ul className="mt-12 border-t border-line" data-id="element-1171" data-mp="bvw3f5q1x8uh">
              {filtered.map(place => <li key={place.id} data-id="element-1172" data-mp="3u1iuci8sgte7">
                  <article className="grid gap-y-5 border-b border-line py-9 lg:grid-cols-[1.6fr_1fr] lg:gap-x-16" data-id="element-1173" data-mp="2vpsnrlqmmz0">
                    <div data-id="element-1174" data-mp="3kchcsnmli3y4">
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1" data-id="element-1175" data-mp="3kb6aytcpn69n">
                        <h3 className="font-display text-[28px] font-300 leading-tight text-ink" data-id="element-1176" data-mp="3p37jq5ao6eux">
                          {place.name}
                        </h3>
                        <span className="label text-olive" data-id="element-1177" data-mp="3fd4ixixb3wm1">{place.area}</span>
                      </div>
                      <p className="mt-4 max-w-prose text-[15.5px] leading-[1.8] text-muted" data-id="element-1178" data-mp="31ddcc394dii2">
                        {place.blurb}
                      </p>
                    </div>
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 self-start lg:grid-cols-2" data-id="element-1179" data-mp="36eaqm4r0dsea">
                      {[['Category', place.category], ['Ages', place.ages], ['Cost', place.cost], ['Indoors', place.tags.includes('Indoor') ? 'Yes' : 'No']].map(([term, value]) => <div key={term} data-id="element-1180" data-mp="37x15yxwh3i92">
                          <dt className="label text-muted/70" data-id="element-1181" data-mp="32vvgra20ud4a">{term}</dt>
                          <dd className="mt-1.5 text-[14px] text-ink" data-id="element-1182" data-mp="2y559tsdy627h">{value}</dd>
                        </div>)}
                    </dl>
                  </article>
                </li>)}
            </ul>}
        </div>
      </section>

      <DirectoryColumns title="Everything else, by category" groups={thingsTier3} data-id="element-1183" data-mp="2p2dtpfa4mi9i" />
      <HubFaqs faqs={thingsFaqs} data-id="element-1184" data-mp="2k6ylp3ysvgl0" />
      <SiblingHubs exclude={['Things to do']} data-id="element-1185" data-mp="2f5rbyzd3k7bp" />
    </main>;
}
function Doorways() {
  return <section aria-labelledby="doorways-h" className="border-y border-line bg-white" data-id="element-1186" data-mp="uorvt4d83l02">
      <div className="mx-auto max-w-shell px-6 py-14 lg:px-10" data-id="element-1187" data-mp="zmjymtfeorj1">
        <DesignNote label="Category doorways, not new pages" note="This hub is too wide for one flat list. In Squarespace these are Categories on the collection, so each gets its own filtered URL for free." data-id="element-1188" data-mp="zp64momneyz5" />
        <h2 id="doorways-h" className="label text-muted" data-id="element-1189" data-mp="by0os9gpk6ou">
          Narrow it down first
        </h2>
        <ul className="mt-9 grid gap-x-12 gap-y-9 sm:grid-cols-3 lg:grid-cols-5" data-id="element-1190" data-mp="gw290xpvec1w">
          {categories.map(cat => <li key={cat.name} className="group relative" data-id="element-1191" data-mp="kxdpacmutkxf">
              <span className={`block h-2 w-12 ${cat.swatch}`} aria-hidden="true" data-id="element-1192" data-mp="pondd8sr8g77" />
              <h3 className="mt-5 font-display text-[25px] font-300 leading-tight text-ink" data-id="element-1193" data-mp="pqqfgr76lzsa">
                <Link to={cat.name === 'Attractions' ? '/attractions' : cat.name === 'Beaches' ? '/beaches' : '/things-to-do'} className="decoration-olive decoration-1 underline-offset-[6px] after:absolute after:inset-0 after:content-[''] group-hover:underline" data-id="element-1194" data-mp="3paklrcqrafru">
                  {cat.name}
                </Link>
              </h3>
              <p className="mt-2 text-[13.5px] leading-snug text-muted" data-id="element-1195" data-mp="3i749snlhuufl">
                {cat.blurb}
              </p>
              <p className="label mt-3 text-muted/70" data-id="element-1196" data-mp="3m7o65oy8tu0o">{cat.count} places</p>
            </li>)}
        </ul>
      </div>
    </section>;
}